import { FunctionComponent, PropsWithChildren, useEffect } from 'react';
import { ContingentCondition } from '@/models/conditionLink';
import { Stack, SvgIcon, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteIcon from '@heroicons/react/24/solid/TrashIcon';
import { BoldLabel } from '@/components/Layout/Textual';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ControlCameraIcon from '@mui/icons-material/ControlCamera';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    ConditionInfoContainer,
    ConditionPreviewContainer,
    FullColumnTile,
    IconButtonContainer,
    IconButtonTop,
    SortNumberContainer,
    SplitIconContainer,
} from './ConditionList';

type ContingentConditionPreviewProps = {
    condition: ContingentCondition;
    onCopy: (template: ContingentCondition) => void;
    onUpdate: (condition: ContingentCondition) => void;
    onDelete: (condition: ContingentCondition) => void;
    onPriorityChange: (
        condition: ContingentCondition,
        priorityChange: 'increase' | 'decrease'
    ) => void;
    position: number;
    isLast: boolean;
    isFirst: boolean;
    disableDrag?: boolean;
    onDragStart?: () => void;
    onDragEnd?: () => void;
};

export const ContingentConditionPreview: FunctionComponent<
    PropsWithChildren<ContingentConditionPreviewProps>
> = ({
    condition,
    onPriorityChange,
    onUpdate,
    onCopy,
    onDelete,
    isFirst,
    isLast,
    position,
    disableDrag,
    onDragStart,
    onDragEnd,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: condition.id });

    useEffect(() => {
        if (isDragging) {
            onDragStart();
        } else {
            onDragEnd();
        }
    }, [isDragging]);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        ...(isDragging && {
            position: 'relative',
            zIndex: 10000,
        }),
    };

    return (
        <ConditionPreviewContainer
            sx={{
                backgroundColor: '#fff',
                zIndex: 10,

                ...style,
            }}
            ref={setNodeRef}
        >
            <SortNumberContainer>
                <Typography variant='h6'>{position}.</Typography>
            </SortNumberContainer>
            <ConditionInfoContainer>
                <Stack
                    spacing={2}
                    sx={{
                        width: '50%',
                    }}
                >
                    <Typography>
                        <BoldLabel>Interner Name:&nbsp;</BoldLabel>
                        {condition.name}
                    </Typography>
                    <Typography>
                        <BoldLabel>Kontingent Neu:&nbsp;</BoldLabel>
                        {condition.maxTicketsPerStartDay}
                    </Typography>
                    {!!condition.maxTicketsForDateRange && (
                        <Typography>
                            <BoldLabel>Gesamt-Kontingent:&nbsp;</BoldLabel>
                            {condition.maxTicketsForDateRange}
                        </Typography>
                    )}
                    <Typography>
                        <BoldLabel>Produktspezifische Zählung:</BoldLabel>{' '}
                        {condition.contingentsProductIdSpecific ? 'Ja' : 'Nein'}
                    </Typography>
                    {(!!condition.startDateValidFrom ||
                        !!condition.startDateValidTo) && (
                        <Typography>
                            <BoldLabel>Gültigkeitszeitraum:</BoldLabel>{' '}
                            konfiguriert
                        </Typography>
                    )}
                </Stack>

                <Stack
                    spacing={2}
                    sx={{
                        width: '50%',
                    }}
                >
                    {(!!condition.validTo || !!condition.validFrom) && (
                        <Typography>
                            <BoldLabel>Verkaufszeitraum:</BoldLabel>{' '}
                            konfiguriert
                        </Typography>
                    )}

                    {!!condition.personTypeIds.length && (
                        <Typography>
                            <BoldLabel>Personentyp(en):</BoldLabel> konfiguriert
                        </Typography>
                    )}
                </Stack>
            </ConditionInfoContainer>
            <SplitIconContainer>
                <IconButtonTop
                    onClick={() => onUpdate(condition)}
                    disableElevation
                >
                    <EditIcon />
                </IconButtonTop>

                <IconButtonContainer
                    onClick={() => {
                        onCopy({
                            ...condition,
                            id: '',
                            name: `${condition.name} (kopie)`,
                        });
                    }}
                    disableElevation
                >
                    <ContentCopyIcon />
                </IconButtonContainer>
            </SplitIconContainer>

            <SplitIconContainer>
                <IconButtonTop
                    onClick={() => {
                        onPriorityChange(condition, 'increase');
                    }}
                    disabled={isFirst}
                    disableElevation
                >
                    <KeyboardArrowUpIcon />
                </IconButtonTop>
                <IconButtonContainer
                    onClick={() => {
                        onPriorityChange(condition, 'decrease');
                    }}
                    disabled={isLast}
                    disableElevation
                >
                    <KeyboardArrowDownIcon />
                </IconButtonContainer>
            </SplitIconContainer>
            <FullColumnTile onClick={() => onDelete(condition)}>
                <SvgIcon>
                    <DeleteIcon />
                </SvgIcon>
            </FullColumnTile>

            <FullColumnTile
                {...attributes}
                {...listeners}
                disabled={disableDrag}
            >
                <SvgIcon>
                    <ControlCameraIcon />
                </SvgIcon>
            </FullColumnTile>
        </ConditionPreviewContainer>
    );
};
